// src/pages/departments/AgriculturalEngineering.js
import { motion } from "framer-motion";

const AgriculturalEngineering = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/agricbg.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Department of Agricultural and Bioenvironmental Engineering
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Excellence in Teaching, Research, and Innovation
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Agricultural and Bioenvironmental Engineering</strong> was
            established in <strong>1981</strong> as one of the pioneering departments within the
            <strong> School of Engineering Technology at the Federal Polytechnic, Bida</strong>.
            Since its inception, the department has evolved into a center of excellence in
            agricultural engineering and environmental sustainability.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            It is committed to producing graduates who are technically sound, innovative, and capable
            of addressing the challenges of modern agriculture, food security, and environmental
            management. Through a balanced blend of theoretical and practical learning, the department
            ensures students are fully prepared for the demands of the industry and society.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed">
            Operating under the guidelines of the <strong>National Board for Technical Education (NBTE)</strong>,
            the department offers accredited <strong>National Diploma (ND)</strong> and
            <strong> Higher National Diploma (HND)</strong> programs aimed at training competent and
            forward-thinking professionals.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <img
              src="/images/hodagric.png"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              Dear students, staff, and stakeholders, it is my honor to welcome you to the
              <strong> Department of Agricultural and Bioenvironmental Engineering</strong> at
              <strong> Federal Polytechnic Bida</strong>. As a department with a rich history of
              innovation and excellence, we are dedicated to shaping the next generation of
              agricultural engineers who will drive sustainable development and technological
              advancement in Nigeria and beyond.
            </p>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              Our focus remains on providing hands-on training, modern facilities, and an environment
              that fosters creativity, research, and entrepreneurship. Through teamwork, dedication,
              and a commitment to excellence, we continue to produce graduates who are globally
              competitive and nationally relevant.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Together, we can harness technology to improve agricultural productivity, promote food
              security, and ensure environmental sustainability.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">— Engr. Dr. Gana Ibrahim Mohammed</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To be a <strong>center of excellence in agricultural and bioenvironmental engineering</strong>,
              providing quality education, research, and innovation that contribute to sustainable
              agricultural development, environmental conservation, and national growth.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To produce <strong>technically skilled and innovative graduates</strong> equipped with
              practical knowledge and ethical values in agricultural and environmental engineering.
              The department is committed to advancing <strong>teaching, research, and community service</strong>
              through modern facilities, competent staff, and industry collaboration—thereby addressing
              real-world challenges in food production, mechanization, and environmental management.
            </p>
          </div>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["agric1.jpg", "agric2.png", "agric3.png", "agric4.png"].map(
              (img, i) => (
                <img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.agricbioenv@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default AgriculturalEngineering;
